import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';

const PermissionsContext = createContext(null);
const POLL_INTERVAL_MS = 30000; // 30s

// Mapping module → préfixes de routes frontend (à masquer + à rediriger)
export const MODULE_ROUTE_PREFIXES = {
  materiel: ['/materiel', '/checklist-materiel'],
  parcellaire: ['/parcellaire', '/parcelles', '/cartographie'],
  interventions: ['/interventions', '/calendrier-interventions'],
  team: ['/team', '/equipe', '/collaborateurs'],
  comptabilite: ['/comptabilite', '/journal-comptable'],
  factures: ['/factures', '/facturation', '/gestion-commerciale', '/facturation-electronique'],
  crm: ['/crm', '/contacts', '/prospection'],
  stocks: ['/stocks'],
  cultures: ['/cultures'],
  fournisseurs: ['/fournisseurs'],
  clients: ['/clients'],
  dashboard: ['/dashboard'],
  rapports: ['/rapports'],
  ai: ['/ai', '/assistant'],
  portail_eta: ['/portail-eta'],
};

export function PermissionsProvider({ children }) {
  const [modulesInterdits, setModulesInterdits] = useState([]);
  const [modulesAutorises, setModulesAutorises] = useState([]);
  const [accesToutes, setAccesToutes] = useState(true);
  const [version, setVersion] = useState('');
  const [loaded, setLoaded] = useState(false);
  const lastVersionRef = useRef('');

  const fetchPermissions = useCallback(async () => {
    try {
      const r = await api.get('/acces-collaborateurs/mon-acces');
      const data = r.data || {};
      const newInterdits = data.modules_interdits || [];
      const newVersion = data.version || '';
      setModulesInterdits(newInterdits);
      setModulesAutorises(data.modules_autorises || []);
      setAccesToutes(!!data.acces_toutes_societes);
      setVersion(newVersion);

      // Notification si version change (hors 1er chargement)
      if (lastVersionRef.current && lastVersionRef.current !== newVersion) {
        toast.info('Vos droits d\'accès ont été mis à jour');
      }
      lastVersionRef.current = newVersion;
    } catch (e) {
      // Silencieux : pas connecté ou endpoint indisponible
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchPermissions();
    const id = setInterval(fetchPermissions, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchPermissions]);

  const isModuleBlocked = useCallback(
    (moduleName) => modulesInterdits.includes(moduleName),
    [modulesInterdits]
  );

  const isPathBlocked = useCallback(
    (pathname) => {
      for (const m of modulesInterdits) {
        const prefixes = MODULE_ROUTE_PREFIXES[m] || [];
        if (prefixes.some((p) => pathname === p || pathname.startsWith(p + '/'))) {
          return m;
        }
      }
      return null;
    },
    [modulesInterdits]
  );

  return (
    <PermissionsContext.Provider
      value={{
        modulesInterdits,
        modulesAutorises,
        accesToutes,
        version,
        loaded,
        isModuleBlocked,
        isPathBlocked,
        refresh: fetchPermissions,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    return {
      modulesInterdits: [],
      modulesAutorises: [],
      accesToutes: true,
      version: '',
      loaded: false,
      isModuleBlocked: () => false,
      isPathBlocked: () => null,
      refresh: () => {},
    };
  }
  return ctx;
}
